import useDayJs from '@/composables/useDayJs'
import { useGetConfigVar } from '@/composables/useGetConfigVar.js'
import { useAuthStore } from '@/stores/authenticated/AuthStore.js'

const translationPath = 'composables.useResolveWorkOrderEvents'

export async function useResolveWorkOrderEvents(t, events) {
  return events
    .map((event) => {
      if (event.type === 'EVENT') {
        return resolveEvent(t, event)
      } else if (event.type === 'COMMENT') {
        return resolveComment(t, event)
      }
      return null
    })
    .filter(Boolean)
}

function getFirstData(event) {
  if (!event.data || event.data.length === 0) {
    return null
  }
  return event.data[0]
}

function resolveEvent(t, event) {
  const firstData = getFirstData(event)

  if (!firstData) {
    return null
  }

  const firstDataResolved = resolveEventData(t, firstData)

  if (!firstDataResolved) {
    return null
  }

  return {
    variant: firstDataResolved.variant ?? 'secondary',
    icon: firstDataResolved.icon ?? 'pencil',
    dateTime: event.created_at,
    title: firstDataResolved.title,
    name: event.user?.name ?? t(`${translationPath}.user.unknown`),
    rows: event.data.flatMap((d) => resolveEventData(t, d)?.rows ?? []),
  }
}

function resolveEventData(t, eventData) {
  switch (eventData.type) {
    case 'ACTIONABLE_DATE_CHANGED':
      return resolveActionableDateChanged(t, eventData)
    case 'CONTRACTOR_ASSIGNED':
      return resolveContractorAssigned(t, eventData)
    case 'DUE_DATE_CHANGED':
      return resolveDueDateChanged(t, eventData)
    case 'ENGINEER_ASSIGNED':
      return resolveEngineerAssigned(t, eventData)
    case 'ETA_DATE_CHANGED':
      return resolveEtaDateChanged(t, eventData)
    case 'ISSUED_WORK_PERMIT':
      return resolveIssuedWorkPermit(t, eventData)
    case 'JOB_SHEET.UPLOADED':
      return resolveJobSheetUploaded(t, eventData)
    case 'PRIORITY_CHANGED':
      return resolvePriorityChanged(t, eventData)
    case 'REQUEST_FOR_SERVICE.SENT':
      return resolveRequestForServiceSent(t, eventData)
    case 'SERVICE_ASSIGNED':
      return resolveServiceAssigned(t, eventData)
    case 'SLA.ALERT_SENT':
      return resolveSlaAlertSent(t, eventData)
    case 'SPEND_LIMIT_INCREASE_APPROVED':
      return resolveSpendLimitApproved(t, eventData)
    case 'SPEND_LIMIT_INCREASE_REJECTED':
      return resolveSpendLimitRejected(t, eventData)
    case 'SPEND_LIMIT_INCREASE_REQUESTED':
      return resolveSpendLimitRequested(t, eventData)
    case 'SPEND_LIMIT_INCREASE_REQUEST_CANCELLED':
      return resolveSpendLimitCancelled(t, eventData)
    case 'STATE_TRANSITION':
      return resolveStateTransition(t, eventData)
    default:
      return null
  }
}

function resolveActionableDateChanged(t, eventData) {
  return {
    title: t(`${translationPath}.types.actionableDateChanged`),
    rows: [
      {
        label: t(`${translationPath}.labels.actionableDate`),
        from: formattedDate(eventData.data?.from) ?? t(`${translationPath}.none`),
        to: formattedDate(eventData.data?.to) ?? t(`${translationPath}.none`),
      },
    ],
  }
}

function resolveContractorAssigned(t, eventData) {
  return {
    icon: 'user-hard-hat',
    title: t(`${translationPath}.types.contractorAssigned`),
    rows: [
      {
        label: t(`${translationPath}.labels.contractor`),
        from: eventData.data?.from?.name ?? t(`${translationPath}.none`),
        to: eventData.data?.to?.name ?? t(`${translationPath}.none`),
      },
    ],
  }
}

function resolveDueDateChanged(t, eventData) {
  return {
    title: t(`${translationPath}.types.dueDateChanged`),
    rows: [
      {
        label: t(`${translationPath}.labels.dueDate`),
        from: formattedDate(eventData.data?.from) ?? t(`${translationPath}.none`),
        to: formattedDate(eventData.data?.to) ?? t(`${translationPath}.none`),
      },
    ],
  }
}

function resolveEngineerAssigned(t, eventData) {
  return {
    icon: 'user-hard-hat',
    title: t(`${translationPath}.types.engineerAssigned`),
    rows: [
      {
        label: t(`${translationPath}.labels.engineer`),
        from: eventData.data?.from?.name ?? t(`${translationPath}.none`),
        to: eventData.data?.to?.name ?? t(`${translationPath}.none`),
      },
    ],
  }
}

function resolveEtaDateChanged(t, eventData) {
  return {
    title: t(`${translationPath}.types.etaDateChanged`),
    rows: [
      {
        label: t(`${translationPath}.labels.etaDate`),
        from: formattedDate(eventData.data?.from) ?? t(`${translationPath}.none`),
        to: formattedDate(eventData.data?.to) ?? t(`${translationPath}.none`),
      },
    ],
  }
}

function resolveIssuedWorkPermit(t, eventData) {
  const permits = eventData.data?.work_permits || []

  return {
    title: t(`${translationPath}.types.issuedWorkPermit`),
    rows: permits.map((permit) => {
      return {
        label: t(`${translationPath}.labels.workPermit`),
        from: null,
        to: permit.name,
      }
    }),
  }
}

function resolveJobSheetUploaded(t, eventData) {
  return {
    title: t(`${translationPath}.types.jobSheetUploaded`),
  }
}

function resolvePriorityChanged(t, eventData) {
  return {
    title: t(`${translationPath}.types.priorityChanged`),
    rows: [
      {
        label: t(`${translationPath}.labels.priority`),
        from: eventData.data?.from?.name ?? t(`${translationPath}.none`),
        to: eventData.data?.to?.name ?? t(`${translationPath}.none`),
      },
    ],
  }
}

function resolveRequestForServiceSent(t, eventData) {
  return {
    title: t(`${translationPath}.types.requestForServiceSent`),
    rows: [
      {
        label: t(`${translationPath}.labels.requestForService`),
        from: null,
        to: eventData.data?.request_for_service?.service?.contractor?.name,
      },
    ],
  }
}

function resolveServiceAssigned(t, eventData) {
  return {
    title: t(`${translationPath}.types.serviceAssigned`),
    rows: [
      {
        label: t(`${translationPath}.labels.service`),
        from: eventData.data?.from?.name ?? t(`${translationPath}.none`),
        to: eventData.data?.to?.name ?? t(`${translationPath}.none`),
      },
    ],
  }
}

function resolveSlaAlertSent(t, eventData) {
  return {
    title: t(`${translationPath}.types.slaAlertSent`),
  }
}

function resolveSpendLimitApproved(t, eventData) {
  return {
    title: t(`${translationPath}.types.spendLimitApproved`),
    rows: [
      {
        label: t(`${translationPath}.labels.spendLimit`),
        from: formattedCurrency(eventData.data?.from) ?? t(`${translationPath}.none`),
        to: formattedCurrency(eventData.data?.to) ?? t(`${translationPath}.none`),
      },
    ],
  }
}

function resolveSpendLimitCancelled(t, eventData) {
  return {
    title: t(`${translationPath}.types.spendLimitCancelled`),
    rows: [
      {
        label: t(`${translationPath}.labels.spendLimit`),
        from: formattedCurrency(eventData.data?.from) ?? t(`${translationPath}.none`),
        to: formattedCurrency(eventData.data?.to) ?? t(`${translationPath}.none`),
      },
    ],
  }
}

function resolveSpendLimitRejected(t, eventData) {
  return {
    title: t(`${translationPath}.types.spendLimitRejected`),
    rows: [
      {
        label: t(`${translationPath}.labels.comment`),
        from: null,
        to: eventData.data?.message,
      },
    ],
  }
}

function resolveSpendLimitRequested(t, eventData) {
  return {
    title: t(`${translationPath}.types.spendLimitRequested`),
    rows: [
      {
        label: t(`${translationPath}.labels.spendLimit`),
        from: formattedCurrency(eventData.data?.from) ?? t(`${translationPath}.none`),
        to: formattedCurrency(eventData.data?.to) ?? t(`${translationPath}.none`),
      },
      {
        label: t(`${translationPath}.labels.comment`),
        from: null,
        to: eventData.data?.message,
      },
    ],
  }
}

function resolveStateTransition(t, eventData) {
  return {
    variant: 'warning',
    icon: 'flag',
    title: t(`${translationPath}.types.stateTransition`),
    rows: [
      {
        label: t(`${translationPath}.labels.status`),
        from: eventData.data?.from?.name ?? t(`${translationPath}.none`),
        to: eventData.data?.to?.name ?? t(`${translationPath}.none`),
      },
    ],
  }
}

function resolveComment(t, event) {
  const firstData = getFirstData(event)

  if (!firstData) {
    return null
  }

  return {
    variant: 'secondary',
    icon: 'comment',
    dateTime: firstData.created_at,
    title: t(`${translationPath}.types.comment`),
    name: firstData.user?.name ?? t(`${translationPath}.unknownUser`),
    rows: [
      {
        label: t(`${translationPath}.labels.comment`),
        from: null,
        to: firstData.message,
      },
      ...firstData.files.map((file) => ({
        label: t(`${translationPath}.labels.file`),
        from: null,
        to: file.file_name,
        clickable: true,
        toClicked: () => {
          window.open(file.url, '_blank')
        },
      })),
    ],
  }
}

function formattedDate(date) {
  return date ? useDayJs(date).format('L') : null
}

function formattedCurrency(value, currency) {
  if (!value) {
    return null
  }

  const authStore = useAuthStore()

  const locale = authStore.user.locale?.toLowerCase() ?? 'en-GB'.toLowerCase()

  if (!currency) {
    currency = useGetConfigVar('DEFAULT_CURRENCY')?.value ?? 'GBP'
  }

  return !Number.isNaN(parseFloat(value))
    ? new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value)
    : null
}
